/**
 * 3D Flood Inundation layer (Flood Risk → "3D Flood Inundation (modelled)") — the
 * #177 Cesium-wow, the reusable 3D foundation (World Terrain + OSM Buildings +
 * extrusion) the other Cesium-wow items (#178–#180) build on, and the #220 per-building
 * solid recolor + multi-region (high-rise) preset support.
 *
 * The mechanic ("watch the water rise"):
 *   1. Enable Cesium World Terrain + depthTestAgainstTerrain via the shared, ref-counted
 *terrainContext (#228) — captured + restored when the LAST 3D layer switches off.
 *   2. Add Cesium OSM Buildings, bounded to the active demo region (shown only when the
 *      camera is near its footprint — no global load).
 *   3. Draw a translucent flood-blue WATER SURFACE at a settable ellipsoid height.
 *      Because the globe depth-tests against terrain, the flat plane is occluded
 *      wherever the ground is higher → it fills the low ground and RISES as the
 *      level increases.
 *   4. Clip the buildings tileset at the same height: submerged geometry is removed
 *      and a flood-blue waterline EDGE marks exactly the buildings that flood.
 *   5. (#220) PER-BUILDING SOLID RECOLOR: low-rise buildings the water reaches are
 *      recolored a solid flood-blue (a Cesium3DTileStyle conditional keyed on the
 *      building's own height), so a flooded house reads as a solid blue mass — not the
 *      thin waterline stub that was visually subtle at the low-rise Kashechewan site.
 *      Taller buildings keep the neutral tint and read as towers standing in the water.
 *
 * Level = sampled-terrain baseline + modelled depth (manual slider OR a modelled map
 * of the upstream gauge — see floodScenario). HONEST framing: a modelled what-if, not
 * an official inundation product (the control legend says so).
 *
 * Multi-region (#220): the active region is a FloodPreset (Kashechewan low-rise OR
 * Calgary high-rise). setPreset re-anchors the ENU frame / water polygon / baseline and
 * (if enabled) rebuilds the buildings + water at the new region.
 *
 * Imperative installer (MapView owns the viewer), mirroring the other layers:
 * setEnabled on the toggle, setLevelDepth from the control, setPreset to switch region,
 * applyRegionGate on camera moves, flyToDemo for the wow, destroy on unmount.
 */
import * as Cesium from 'cesium';
import {
  presetById,
  FLOOD_DEFAULT_PRESET_ID,
  FLOOD_LOWRISE_CEILING_M,
  floodSurfaceHeightM,
  type FloodPreset,
} from '../floodScenario';
import {
  FLOOD_WATER_CSS,
  FLOOD_WATER_ALPHA,
  FLOOD_BUILDING_EDGE_CSS,
  FLOOD_BUILDING_EDGE_WIDTH,
  FLOOD_BUILDING_TINT_CSS,
  FLOOD_BUILDING_FLOODED_CSS,
} from '../styles/floodInundation';
import { acquireWorldTerrain, releaseWorldTerrain } from '../lib/terrainContext';

/** Above this camera height (m) the demo buildings/water are hidden so the global
 *  OSM Buildings tileset never streams nationally — bounds the 3D cost to the
 *  regional view. */
const REGION_GATE_MAX_HEIGHT_M = 120_000;

export interface FloodInundationLayerHandle {
  /** Turn the whole mechanic on/off (terrain swap, buildings, water). */
  setEnabled(enabled: boolean): void;
  /** Update the modelled flood depth (metres above the sampled ground baseline). */
  setLevelDepth(depthM: number): void;
  /** Switch the active demo region (Kashechewan ↔ Calgary high-rise). Resets the
   *  modelled depth to the preset's default. */
  setPreset(presetId: string): void;
  /** Show/hide buildings + water based on camera proximity to the demo region. */
  applyRegionGate(): void;
  /** Oblique fly-to the active demo region for the wow. */
  flyToDemo(): void;
  /** True once terrain has resolved + the baseline has been sampled. */
  isReady(): boolean;
  destroy(): void;
}

/** Test seams (all optional → real Cesium in production). Mirror the
 *  CameraViewshedLayer pattern so the region gate can be exercised headlessly
 *  without an ion token / WebGL. */
export interface FloodInundationLayerOptions {
  /** Skip the World Terrain swap (no ion token needed in tests). */
  skipTerrainSwap?: boolean;
  /** Inject the terrain baseline sampler in place of Cesium's. */
  terrainSampler?: (
    cartographics: Cesium.Cartographic[],
  ) => Promise<Cesium.Cartographic[]>;
  /** Inject the OSM Buildings tileset factory (returns a fake tileset in tests). */
  buildingsFactory?: () => Promise<Cesium.Cesium3DTileset>;
}

export function installFloodInundationLayer(
  viewer: Cesium.Viewer,
  opts: FloodInundationLayerOptions = {},
): FloodInundationLayerHandle {
  const waterColor = Cesium.Color.fromCssColorString(FLOOD_WATER_CSS).withAlpha(
    FLOOD_WATER_ALPHA,
  );
  const edgeColor = Cesium.Color.fromCssColorString(FLOOD_BUILDING_EDGE_CSS);
  const neutralTintCss = Cesium.Color.fromCssColorString(FLOOD_BUILDING_TINT_CSS).toCssHexString();
  const floodedTintCss = Cesium.Color.fromCssColorString(FLOOD_BUILDING_FLOODED_CSS).toCssHexString();

  const ds = new Cesium.CustomDataSource('flood-inundation');
  void viewer.dataSources.add(ds);

  let enabled = false;
  let ready = false;
  let setupGen = 0;
  let preset: FloodPreset = presetById(FLOOD_DEFAULT_PRESET_ID);
  let depthM = preset.defaultDepthM;
  let baselineM = preset.baselineFallbackM;

  let buildings: Cesium.Cesium3DTileset | null = null;
  let waterEntity: Cesium.Entity | null = null;
  let clippingPlane: Cesium.ClippingPlane | null = null;

  // Geometry derived from the active preset (recomputed in configure()): the local ENU
  // frame (building clipping plane lives here), the water polygon corners, and the
  // region-gate rectangle.
  let enuMatrix!: Cesium.Matrix4;
  let corners!: Cesium.Cartesian3[];
  let demoRect!: Cesium.Rectangle;

  /** Recompute the preset-derived geometry. The clipping plane is in a local ENU frame
   *  at the demo centre at ellipsoid height 0 so "horizontal at the demo area" is a
   *  single plane (valid over the ~3–10 km footprint, ignoring earth curvature). */
  const configure = (next: FloodPreset): void => {
    preset = next;
    const enuOrigin = Cesium.Cartesian3.fromDegrees(preset.center.lon, preset.center.lat, 0);
    enuMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(enuOrigin);
    corners = Cesium.Cartesian3.fromDegreesArray([
      preset.bounds.west, preset.bounds.south,
      preset.bounds.east, preset.bounds.south,
      preset.bounds.east, preset.bounds.north,
      preset.bounds.west, preset.bounds.north,
    ]);
    demoRect = Cesium.Rectangle.fromDegrees(
      preset.bounds.west, preset.bounds.south,
      preset.bounds.east, preset.bounds.north,
    );
  };
  configure(preset);

  // Whether THIS layer currently holds a World Terrain reference. The shared,
  // reference-counted terrainContext owns the actual capture/restore of the operator's
  // globe, so the flood and camera-viewshed layers can both rely on World Terrain at
  // once and it survives until the LAST 3D layer switches off. (OSM Buildings stay
  // managed locally here — they are flood-only — see teardownBuildings.)
  let holdingTerrain = false;

  const currentSurfaceHeight = (): number => floodSurfaceHeightM(baselineM, depthM);

  /** Build (or rebuild) the translucent water plane at the current surface height. */
  const renderWater = (): void => {
    const height = currentSurfaceHeight();
    if (!waterEntity) {
      waterEntity = ds.entities.add({
        id: 'flood-water-surface',
        polygon: {
          hierarchy: new Cesium.PolygonHierarchy(corners),
          height,
          perPositionHeight: false,
          material: waterColor,
          // Outline off — the surface reads as a water body, the buildings carry
          // the waterline edge.
          outline: false,
        },
      });
    } else if (waterEntity.polygon) {
      waterEntity.polygon.height = new Cesium.ConstantProperty(height);
    }
  };

  /** Point the building clipping plane at the current surface height. The plane is
   *  in the ENU frame (origin at ellipsoid height 0) so distance = -surfaceHeight
   *  clips geometry BELOW the surface (the flooded part). */
  const updateClipping = (): void => {
    if (clippingPlane) clippingPlane.distance = -currentSurfaceHeight();
  };

  /** Build the per-building recolor style (#220). FLOOD-AFFECTED low-rise buildings
   *  (height ≤ the low-rise ceiling, when the water is present) get the solid flood
   *  tint; everything else keeps the neutral tint. Keyed on the building's own height
   *  (`cesium#estimatedHeight`) so it is per-building, with no terrain sampling. */
  const buildingStyle = (): Cesium.Cesium3DTileStyle => {
    if (depthM <= 0) {
      return new Cesium.Cesium3DTileStyle({ color: `color('${neutralTintCss}')` });
    }
    return new Cesium.Cesium3DTileStyle({
      color: {
        conditions: [
          // Low-rise + water present → flood-affected solid recolor.
          [`\${feature['cesium#estimatedHeight']} <= ${FLOOD_LOWRISE_CEILING_M}`, `color('${floodedTintCss}')`],
          // Taller buildings (and features with no height) → neutral standing tint.
          ['true', `color('${neutralTintCss}')`],
        ],
      },
    });
  };

  /** Re-apply the per-building recolor (after a depth change or buildings load). */
  const applyBuildingStyle = (): void => {
    if (buildings) buildings.style = buildingStyle();
  };

  /** Apply the regional show-gate: buildings + water render only when the camera is
   *  near the demo footprint and below the height ceiling. */
  const applyRegionGate = (): void => {
    if (!enabled) {
      ds.show = false;
      if (buildings) buildings.show = false;
      return;
    }
    const rect = viewer.camera.computeViewRectangle();
    const height = viewer.camera.positionCartographic.height;
    const near =
      height < REGION_GATE_MAX_HEIGHT_M &&
      !!rect &&
      !!Cesium.Rectangle.intersection(rect, demoRect, new Cesium.Rectangle());
    ds.show = near;
    if (buildings) buildings.show = near;
  };

  /** Async setup: swap in World Terrain, sample the baseline, add OSM Buildings with
   *  the flood clip + per-building recolor. Guarded by a generation counter so a rapid
   *  off→on or preset switch can't leave a stale async result attached. */
  const setup = async (gen: number): Promise<void> => {
    // 1. World Terrain (saved + restored on disable). Without an ion token this
    //    throws — degrade to the existing (flat) terrain rather than crashing.
    if (!opts.skipTerrainSwap) {
      try {
        const terrain = await Cesium.createWorldTerrainAsync();
        if (gen !== setupGen) return;
        if (!holdingTerrain) {
          acquireWorldTerrain(viewer, terrain);
          holdingTerrain = true;
        }
      } catch (err) {
        console.error('flood-inundation: World Terrain unavailable (ion token?)', err);
      }
    }

    // 2. Sample the ground baseline at the demo centre (ellipsoid height). Falls
    //    back to the preset's configured constant if sampling fails.
    try {
      const sampler =
        opts.terrainSampler ??
        ((cartographics: Cesium.Cartographic[]) =>
          Cesium.sampleTerrainMostDetailed(viewer.terrainProvider, cartographics));
      const [sampled] = await sampler([
        Cesium.Cartographic.fromDegrees(preset.center.lon, preset.center.lat),
      ]);
      if (gen !== setupGen) return;
      if (sampled && Number.isFinite(sampled.height)) baselineM = sampled.height;
    } catch (err) {
      console.error('flood-inundation: terrain sample failed, using fallback', err);
    }

    // 3. OSM Buildings, clipped at the flood surface (submerged geometry removed +
    //    a flood-blue waterline edge) + the #220 per-building solid recolor.
    try {
      const tileset = await (opts.buildingsFactory
        ? opts.buildingsFactory()
        : Cesium.createOsmBuildingsAsync());
      if (gen !== setupGen) {
        // Toggled off / preset switched mid-load — discard.
        return;
      }
      clippingPlane = new Cesium.ClippingPlane(
        new Cesium.Cartesian3(0, 0, 1),
        -currentSurfaceHeight(),
      );
      tileset.clippingPlanes = new Cesium.ClippingPlaneCollection({
        modelMatrix: enuMatrix,
        planes: [clippingPlane],
        edgeColor,
        edgeWidth: FLOOD_BUILDING_EDGE_WIDTH,
        unionClippingRegions: false,
      });
      buildings = tileset;
      applyBuildingStyle();
      // #336 — start hidden so the global OSM Buildings tileset never renders before the
      // region gate below decides; applyRegionGate() (end of setup, and on every camera
      // settle via MapView) flips it on only when the camera is within the demo region.
      tileset.show = false;
      viewer.scene.primitives.add(tileset);
    } catch (err) {
      console.error('flood-inundation: OSM Buildings unavailable (ion token?)', err);
    }

    renderWater();
    updateClipping();
    ready = true;
    applyRegionGate();
  };

  const teardownBuildings = (): void => {
    if (buildings) {
      if (!viewer.isDestroyed()) viewer.scene.primitives.remove(buildings);
      buildings = null;
    }
    clippingPlane = null;
  };

  /** Tear down the per-region render artifacts (buildings + water) without touching the
   *  shared terrain hold — used on disable and on preset switch. */
  const teardownRegion = (): void => {
    teardownBuildings();
    ds.entities.removeAll();
    waterEntity = null;
    ds.show = false;
  };

  const restoreTerrain = (): void => {
    if (!holdingTerrain) return;
    holdingTerrain = false;
    releaseWorldTerrain(viewer);
  };

  return {
    setEnabled(next: boolean): void {
      if (next === enabled) return;
      enabled = next;
      if (next) {
        ready = false;
        void setup(++setupGen);
      } else {
        setupGen++; // invalidate any in-flight setup
        ready = false;
        teardownRegion();
        restoreTerrain();
      }
    },
    setLevelDepth(next: number): void {
      depthM = next;
      if (!enabled) return;
      renderWater();
      updateClipping();
      applyBuildingStyle();
    },
    setPreset(presetId: string): void {
      const next = presetById(presetId);
      if (next.id === preset.id) return;
      configure(next);
      // Reset the modelled depth to the new region's default (a visible flood).
      depthM = next.defaultDepthM;
      // Re-baseline to the new region's fallback until runtime sampling overrides it.
      baselineM = next.baselineFallbackM;
      if (!enabled) return;
      // Rebuild the per-region artifacts at the new location (keep the terrain hold).
      ready = false;
      teardownRegion();
      void setup(++setupGen);
    },
    applyRegionGate,
    flyToDemo(): void {
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(
          preset.view.lon,
          preset.view.lat,
          preset.view.heightM,
        ),
        orientation: {
          heading: Cesium.Math.toRadians(preset.view.headingDeg),
          pitch: Cesium.Math.toRadians(preset.view.pitchDeg),
          roll: 0,
        },
        duration: 2.5,
      });
    },
    isReady(): boolean {
      return ready;
    },
    destroy(): void {
      setupGen++;
      teardownBuildings();
      restoreTerrain();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
